import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Plus, Search, Filter, Download, Phone, Mail, MapPin,
  MessageCircle, Edit3, Trash2, Eye, X, ChevronDown, Tag, Building2,
  FileText, Receipt, Shield, Calendar, Clock, History, DollarSign
} from 'lucide-react';
import { useAdminStore } from '../store/adminStore';
import { formatCurrency, formatDate, formatDateTime, getRelativeTime, generateId, paginate } from '../utils/helpers';
import { toast } from '../components/Toast';
import { Pagination } from '../components/UIComponents';
import type { Customer } from '../types';

const PER_PAGE = 9;

type TabId = 'overview' | 'projects' | 'invoices' | 'warranty' | 'payments' | 'timeline';

export default function CustomersPage() {
  const { customers, addCustomer, updateCustomer, deleteCustomer, projects, invoices, warranties } = useAdminStore();
  const [search, setSearch] = useState('');
  const [showAdd, setShowAdd] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [filterTag, setFilterTag] = useState('all');
  const [page, setPage] = useState(1);
  const [newCustomer, setNewCustomer] = useState({
    name: '', mobile: '', whatsapp: '', email: '', address: '', city: '', tags: [] as string[], notes: [] as string[],
  });

  const allTags = [...new Set(customers.flatMap(c => c.tags || []))];
  const filtered = customers.filter(c => {
    const matchSearch = c.name.toLowerCase().includes(search.toLowerCase()) || c.mobile.includes(search) || (c.email || '').toLowerCase().includes(search.toLowerCase());
    const matchTag = filterTag === 'all' || (c.tags || []).includes(filterTag);
    return matchSearch && matchTag;
  });

  // Reset to page 1 when filters change
  useEffect(() => { setPage(1); }, [search, filterTag]);

  const { items: pagedCustomers, pagination } = useMemo(
    () => paginate(filtered, page, PER_PAGE),
    [filtered, page]
  );

  // Customer related data
  const customerProjects = selectedCustomer ? projects.filter(p => p.customerId === selectedCustomer.id) : [];
  const customerInvoices = selectedCustomer ? invoices.filter(i => i.customerId === selectedCustomer.id) : [];
  const customerWarranties = selectedCustomer ? warranties.filter(w => w.customerId === selectedCustomer.id) : [];
  const customerPayments = customerInvoices.flatMap(inv => inv.payments.map(p => ({ ...p, invoiceNumber: inv.number })));
  const customerTimeline = [
    ...customerProjects.map(p => ({ type: 'project', date: p.createdAt, label: `Project: ${p.name}`, status: p.status })),
    ...customerInvoices.map(i => ({ type: 'invoice', date: i.createdAt, label: `Invoice: ${i.number}`, status: i.paymentStatus })),
    ...customerPayments.map(p => ({ type: 'payment', date: p.date, label: `Payment: Rs. ${p.amount.toLocaleString()}`, status: 'success' })),
    ...customerWarranties.map(w => ({ type: 'warranty', date: w.installationDate, label: `Warranty: ${w.products[0]?.productName || 'Product'}`, status: w.status })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  const handleAdd = () => {
    if (!newCustomer.name.trim()) { toast.error('Validation Error', 'Customer name is required.'); return; }
    if (!newCustomer.mobile.trim()) { toast.error('Validation Error', 'Mobile number is required.'); return; }
    const customer: Customer = {
      id: generateId(),
      ...newCustomer,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      totalProjects: 0,
      totalInvoices: 0,
      totalSpent: 0,
    };
    addCustomer(customer);
    toast.success('Customer Added', `${newCustomer.name} has been added to the CRM.`);
    setNewCustomer({ name: '', mobile: '', whatsapp: '', email: '', address: '', city: '', tags: [], notes: [] });
    setShowAdd(false);
  };

  const tabs: { id: TabId; label: string; icon: React.ElementType; count?: number }[] = [
    { id: 'overview', label: 'Overview', icon: Eye },
    { id: 'projects', label: 'Projects', icon: FileText, count: customerProjects.length },
    { id: 'invoices', label: 'Invoices', icon: Receipt, count: customerInvoices.length },
    { id: 'warranty', label: 'Warranty', icon: Shield, count: customerWarranties.length },
    { id: 'payments', label: 'Payments', icon: DollarSign, count: customerPayments.length },
    { id: 'timeline', label: 'Timeline', icon: Clock, count: customerTimeline.length },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold text-gray-900 tracking-tight">Customers</h1>
          <p className="text-[13px] text-gray-400 mt-0.5">{customers.length} total customers</p>
        </div>
        <button onClick={() => setShowAdd(true)} className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 active:scale-[0.98] transition-all flex items-center gap-2">
          <Plus className="w-4 h-4" />Add Customer
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, phone, or email..."
            className="w-full h-10 pl-10 pr-4 rounded-xl bg-white border border-gray-200/60 text-[13px] text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-900/5 focus:border-gray-300 transition-all" />
        </div>
        <div className="relative">
          <select value={filterTag} onChange={(e) => setFilterTag(e.target.value)}
            className="h-10 pl-3 pr-8 rounded-xl bg-white border border-gray-200/60 text-[13px] text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900/5 appearance-none cursor-pointer">
            <option value="all">All Tags</option>
            {allTags.map(tag => <option key={tag} value={tag}>{tag}</option>)}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
        </div>
        <button className="h-10 px-4 rounded-xl bg-white border border-gray-200/60 text-[13px] text-gray-600 font-medium hover:bg-gray-50 transition-all flex items-center gap-2">
          <Download className="w-4 h-4" />Export
        </button>
      </div>

      {/* Customer Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {pagedCustomers.map((customer, i) => (
          <motion.div
            key={customer.id}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.03 }}
            className="bg-white/80 backdrop-blur-xl rounded-2xl border border-gray-200/60 p-5 hover:shadow-lg hover:shadow-gray-200/40 transition-all cursor-pointer group"
            onClick={() => { setSelectedCustomer(customer); setShowDetail(true); setActiveTab('overview'); }}
          >
            <div className="flex items-start gap-3 mb-4">
              <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center text-white text-[14px] font-semibold flex-shrink-0">
                {customer.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-[14px] font-semibold text-gray-900 truncate">{customer.name}</h3>
                <p className="text-[12px] text-gray-400 mt-0.5">{customer.id}</p>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={(e) => { e.stopPropagation(); }} className="p-1.5 rounded-lg hover:bg-gray-100"><Edit3 className="w-3.5 h-3.5 text-gray-400" /></button>
                <button onClick={(e) => { e.stopPropagation(); deleteCustomer(customer.id); }} className="p-1.5 rounded-lg hover:bg-red-50"><Trash2 className="w-3.5 h-3.5 text-red-400" /></button>
              </div>
            </div>
            <div className="space-y-2 mb-4">
              <div className="flex items-center gap-2 text-[12px] text-gray-500">
                <Phone className="w-3.5 h-3.5 text-gray-400" />{customer.mobile}
                {customer.whatsapp && <a href={`https://wa.me/${customer.whatsapp}`} onClick={e => e.stopPropagation()} className="ml-auto"><MessageCircle className="w-3.5 h-3.5 text-emerald-500 hover:text-emerald-600" /></a>}
              </div>
              {customer.email && <div className="flex items-center gap-2 text-[12px] text-gray-500"><Mail className="w-3.5 h-3.5 text-gray-400" />{customer.email}</div>}
              {customer.city && <div className="flex items-center gap-2 text-[12px] text-gray-500"><MapPin className="w-3.5 h-3.5 text-gray-400" />{customer.city}</div>}
            </div>
            {(customer.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-4">
                {customer.tags!.map(tag => (
                  <span key={tag} className="px-2 py-0.5 rounded-full bg-gray-100 text-[10px] font-medium text-gray-600">{tag}</span>
                ))}
              </div>
            )}
            <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
              <div className="text-[12px] text-gray-400">{customer.totalProjects} projects</div>
              <div className="text-[14px] font-semibold text-gray-900">{formatCurrency(customer.totalSpent)}</div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Pagination */}
      <Pagination pagination={pagination} onPageChange={setPage} />

      {/* Detail Modal */}
      {showDetail && selectedCustomer && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowDetail(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-3xl bg-white rounded-3xl shadow-2xl border border-gray-200/60 max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div className="p-6 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center text-white text-[16px] font-semibold">
                  {selectedCustomer.name.charAt(0)}
                </div>
                <div>
                  <h2 className="text-[18px] font-semibold text-gray-900">{selectedCustomer.name}</h2>
                  <p className="text-[12px] text-gray-400">{selectedCustomer.id}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a href={`https://wa.me/${selectedCustomer.whatsapp || selectedCustomer.mobile}`} target="_blank" rel="noopener noreferrer"
                  className="h-9 px-3 rounded-xl bg-emerald-50 text-emerald-600 text-[12px] font-medium hover:bg-emerald-100 flex items-center gap-1.5">
                  <MessageCircle className="w-3.5 h-3.5" />WhatsApp
                </a>
                <button onClick={() => setShowDetail(false)} className="p-1.5 rounded-xl hover:bg-gray-100"><X className="w-5 h-5 text-gray-400" /></button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-6 pt-4 overflow-x-auto">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-[12px] font-medium whitespace-nowrap transition-all ${activeTab === tab.id ? 'bg-gray-900 text-white' : 'text-gray-500 hover:bg-gray-100'}`}>
                    <Icon className="w-3.5 h-3.5" />{tab.label}
                    {tab.count !== undefined && <span className="ml-1 px-1.5 py-0.5 rounded-full bg-white/20 text-[10px]">{tab.count}</span>}
                  </button>
                );
              })}
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {activeTab === 'overview' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <div className="p-3 bg-gray-50 rounded-xl"><span className="text-[11px] text-gray-400 uppercase font-semibold">Mobile</span><p className="text-[13px] text-gray-700">{selectedCustomer.mobile}</p></div>
                    <div className="p-3 bg-gray-50 rounded-xl"><span className="text-[11px] text-gray-400 uppercase font-semibold">Email</span><p className="text-[13px] text-gray-700">{selectedCustomer.email || '—'}</p></div>
                    <div className="p-3 bg-gray-50 rounded-xl"><span className="text-[11px] text-gray-400 uppercase font-semibold">Address</span><p className="text-[13px] text-gray-700">{selectedCustomer.address || '—'}, {selectedCustomer.city || ''}</p></div>
                  </div>
                  <div className="space-y-3">
                    <div className="p-3 bg-gray-50 rounded-xl"><span className="text-[11px] text-gray-400 uppercase font-semibold">Total Spent</span><p className="text-[16px] font-bold text-gray-900">{formatCurrency(selectedCustomer.totalSpent)}</p></div>
                    <div className="p-3 bg-gray-50 rounded-xl"><span className="text-[11px] text-gray-400 uppercase font-semibold">Projects</span><p className="text-[16px] font-bold text-gray-900">{selectedCustomer.totalProjects}</p></div>
                    <div className="p-3 bg-gray-50 rounded-xl"><span className="text-[11px] text-gray-400 uppercase font-semibold">Invoices</span><p className="text-[16px] font-bold text-gray-900">{selectedCustomer.totalInvoices}</p></div>
                  </div>
                  {(selectedCustomer.tags || []).length > 0 && (
                    <div className="col-span-2 pt-3 border-t border-gray-100">
                      <div className="flex flex-wrap gap-1.5">
                        {selectedCustomer.tags!.map(tag => (
                          <span key={tag} className="px-2.5 py-1 rounded-full bg-gray-100 text-[11px] font-medium text-gray-600">{tag}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'projects' && (
                <div className="space-y-3">
                  {customerProjects.length === 0 ? (
                    <div className="py-8 text-center"><FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" /><p className="text-[13px] text-gray-400">No projects yet</p></div>
                  ) : customerProjects.map(p => (
                    <div key={p.id} className="p-4 bg-gray-50 rounded-xl flex items-center gap-3">
                      <FileText className="w-5 h-5 text-gray-400" />
                      <div className="flex-1"><div className="text-[13px] font-medium text-gray-900">{p.name}</div><div className="text-[11px] text-gray-400">{formatDate(p.createdAt)}</div></div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border ${p.status === 'completed' ? 'bg-emerald-50 text-emerald-600 border-emerald-200' : 'bg-blue-50 text-blue-600 border-blue-200'}`}>{getStatusLabel(p.status)}</span>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'invoices' && (
                <div className="space-y-3">
                  {customerInvoices.length === 0 ? (
                    <div className="py-8 text-center"><Receipt className="w-8 h-8 text-gray-300 mx-auto mb-2" /><p className="text-[13px] text-gray-400">No invoices yet</p></div>
                  ) : customerInvoices.map(inv => (
                    <div key={inv.id} className="p-4 bg-gray-50 rounded-xl flex items-center gap-3">
                      <Receipt className="w-5 h-5 text-gray-400" />
                      <div className="flex-1"><div className="text-[13px] font-medium text-gray-900">{inv.number}</div><div className="text-[11px] text-gray-400">{formatDate(inv.createdAt)}</div></div>
                      <div className="text-right"><div className="text-[13px] font-semibold text-gray-900">{formatCurrency(inv.total)}</div><span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${inv.paymentStatus === 'paid' ? 'bg-emerald-50 text-emerald-600' : 'bg-amber-50 text-amber-600'}`}>{inv.paymentStatus}</span></div>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'warranty' && (
                <div className="space-y-3">
                  {customerWarranties.length === 0 ? (
                    <div className="py-8 text-center"><Shield className="w-8 h-8 text-gray-300 mx-auto mb-2" /><p className="text-[13px] text-gray-400">No warranties yet</p></div>
                  ) : customerWarranties.map(w => (
                    <div key={w.id} className="p-4 bg-gray-50 rounded-xl flex items-center gap-3">
                      <Shield className="w-5 h-5 text-gray-400" />
                      <div className="flex-1"><div className="text-[13px] font-medium text-gray-900">{w.products[0]?.productName}</div><div className="text-[11px] text-gray-400">Expires: {formatDate(w.expiryDate)}</div></div>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${w.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>{w.status}</span>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'payments' && (
                <div className="space-y-3">
                  {customerPayments.length === 0 ? (
                    <div className="py-8 text-center"><DollarSign className="w-8 h-8 text-gray-300 mx-auto mb-2" /><p className="text-[13px] text-gray-400">No payments yet</p></div>
                  ) : customerPayments.map((p, i) => (
                    <div key={p.id || i} className="p-4 bg-gray-50 rounded-xl flex items-center gap-3">
                      <DollarSign className="w-5 h-5 text-emerald-500" />
                      <div className="flex-1"><div className="text-[13px] font-medium text-gray-900">{formatCurrency(p.amount)}</div><div className="text-[11px] text-gray-400">{p.invoiceNumber} · {p.method.replace('_', ' ')}</div></div>
                      <span className="text-[12px] text-gray-400">{formatDate(p.date)}</span>
                    </div>
                  ))}
                </div>
              )}

              {activeTab === 'timeline' && (
                <div className="space-y-1">
                  {customerTimeline.length === 0 ? (
                    <div className="py-8 text-center"><Clock className="w-8 h-8 text-gray-300 mx-auto mb-2" /><p className="text-[13px] text-gray-400">No activity yet</p></div>
                  ) : customerTimeline.map((event, i) => (
                    <div key={i} className="flex items-start gap-3 py-3 border-b border-gray-50 last:border-0">
                      <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${event.type === 'payment' ? 'bg-emerald-400' : event.type === 'project' ? 'bg-blue-400' : event.type === 'invoice' ? 'bg-amber-400' : 'bg-violet-400'}`} />
                      <div className="flex-1"><div className="text-[13px] font-medium text-gray-900">{event.label}</div></div>
                      <span className="text-[11px] text-gray-400">{getRelativeTime(event.date)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}

      {/* Add Customer Modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/20 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => setShowAdd(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-gray-200/60 p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-[18px] font-semibold text-gray-900">Add Customer</h2>
              <button onClick={() => setShowAdd(false)} className="p-1.5 rounded-xl hover:bg-gray-100"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Name *</label>
                  <input value={newCustomer.name} onChange={e => setNewCustomer({ ...newCustomer, name: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" placeholder="Company or person name" /></div>
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Mobile *</label>
                  <input value={newCustomer.mobile} onChange={e => setNewCustomer({ ...newCustomer, mobile: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" placeholder="07X XXX XXXX" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">WhatsApp</label>
                  <input value={newCustomer.whatsapp} onChange={e => setNewCustomer({ ...newCustomer, whatsapp: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" /></div>
                <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Email</label>
                  <input value={newCustomer.email} onChange={e => setNewCustomer({ ...newCustomer, email: e.target.value })}
                    className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" /></div>
              </div>
              <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">Address</label>
                <input value={newCustomer.address} onChange={e => setNewCustomer({ ...newCustomer, address: e.target.value })}
                  className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" /></div>
              <div><label className="block text-[11px] font-semibold text-gray-400 uppercase tracking-wider mb-1.5">City</label>
                <input value={newCustomer.city} onChange={e => setNewCustomer({ ...newCustomer, city: e.target.value })}
                  className="w-full h-10 px-3 rounded-xl bg-gray-50 border border-gray-200/60 text-[13px] focus:outline-none focus:ring-2 focus:ring-gray-900/5" /></div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowAdd(false)} className="h-10 px-5 rounded-xl bg-gray-100 text-gray-600 text-[13px] font-medium hover:bg-gray-200 transition-all">Cancel</button>
              <button onClick={handleAdd} disabled={!newCustomer.name || !newCustomer.mobile}
                className="h-10 px-5 rounded-xl bg-gray-900 text-white text-[13px] font-medium hover:bg-gray-800 disabled:opacity-50 transition-all">Add Customer</button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
}
